/**
 * Assistant UI state.
 *
 * Not persisted. Runs are keyed by thread id and the stream is driven by a
 * store action rather than an effect, so switching notes or collapsing the
 * panel cannot abort a run that is halfway through editing something.
 */
import { create } from "zustand";
import type { AgentEvent, ChatMessageDto, ChatThreadDto } from "@hydra-notes/shared";
import * as api from "@/data/chat";
import { scopeKey, type ChatScope } from "@/data/chat";
import { toast } from "@/ui/toast";

export interface ToolCall {
  id: string;
  name: string;
  input: unknown;
  status: "running" | "ok" | "error";
  result?: string;
  truncated?: boolean;
  durationMs?: number;
}

export interface RunState {
  runId: string;
  threadId: string;
  scopeKey: string;
  status: "starting" | "streaming" | "error";
  text: string;
  tools: ToolCall[];
  notice: string | null;
  error: string | null;
  startedAt: number;
}

/** Stop handles live outside the store so selectors never compare them. */
const stoppers = new Map<string, () => void>();

interface ChatState {
  threads: Record<string, ChatThreadDto[]>;
  messages: Record<string, ChatMessageDto[]>;
  activeThread: Record<string, string | null>;
  runs: Record<string, RunState>;
  models: { list: { id: string; label: string; blurb: string }[]; current: string } | null;

  loadModels: () => Promise<void>;
  loadThreads: (scope: ChatScope) => Promise<void>;
  loadMessages: (threadId: string) => Promise<void>;
  selectThread: (scope: ChatScope, threadId: string | null) => void;
  newThread: (scope: ChatScope) => Promise<string | null>;
  archiveThread: (scope: ChatScope, threadId: string) => Promise<void>;
  removeThread: (scope: ChatScope, threadId: string) => Promise<void>;
  setModel: (model: string) => void;
  /** Resolves true when the run actually started; false leaves the draft with the caller. */
  send: (scope: ChatScope, text: string) => Promise<boolean>;
  stop: (threadId: string) => void;
}

export const useChat = create<ChatState>()((set, get) => ({
  threads: {},
  messages: {},
  activeThread: {},
  runs: {},
  models: null,

  loadModels: async () => {
    if (get().models) return;
    try {
      const { models, defaultModel } = await api.listModels();
      set({ models: { list: models.map((m) => ({ id: m.id, label: m.label, blurb: m.blurb })), current: defaultModel } });
    } catch {
      // the panel still works; the picker just will not show
    }
  },

  loadThreads: async (scope) => {
    const key = scopeKey(scope);
    try {
      const { threads } = await api.listThreads(scope);
      set((s) => ({ threads: { ...s.threads, [key]: threads } }));
      if (!get().activeThread[key] && threads[0]) {
        get().selectThread(scope, threads[0].id);
      }
    } catch {
      set((s) => ({ threads: { ...s.threads, [key]: s.threads[key] ?? [] } }));
    }
  },

  loadMessages: async (threadId) => {
    if (get().messages[threadId]) return;
    try {
      const { messages } = await api.listMessages(threadId);
      set((s) => ({ messages: { ...s.messages, [threadId]: messages } }));
    } catch {
      // leave it empty; the composer still works
    }
  },

  selectThread: (scope, threadId) => {
    set((s) => ({ activeThread: { ...s.activeThread, [scopeKey(scope)]: threadId } }));
    if (threadId) void get().loadMessages(threadId);
  },

  newThread: async (scope) => {
    try {
      const { thread } = await api.createThread(scope);
      const key = scopeKey(scope);
      set((s) => ({
        threads: { ...s.threads, [key]: [thread, ...(s.threads[key] ?? [])] },
        activeThread: { ...s.activeThread, [key]: thread.id },
        messages: { ...s.messages, [thread.id]: [] },
      }));
      return thread.id;
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "could not start a conversation");
      return null;
    }
  },

  archiveThread: async (scope, threadId) => {
    const key = scopeKey(scope);
    await api.patchThread(threadId, { archived: true }).catch(() => {});
    set((s) => ({
      threads: { ...s.threads, [key]: (s.threads[key] ?? []).filter((t) => t.id !== threadId) },
      activeThread: { ...s.activeThread, [key]: s.activeThread[key] === threadId ? null : (s.activeThread[key] ?? null) },
    }));
    toast.info("Conversation archived");
  },

  removeThread: async (scope, threadId) => {
    const key = scopeKey(scope);
    await api.deleteThread(threadId).catch(() => {});
    set((s) => ({
      threads: { ...s.threads, [key]: (s.threads[key] ?? []).filter((t) => t.id !== threadId) },
      activeThread: { ...s.activeThread, [key]: s.activeThread[key] === threadId ? null : (s.activeThread[key] ?? null) },
    }));
  },

  setModel: (model) => set((s) => (s.models ? { models: { ...s.models, current: model } } : s)),

  send: async (scope, text) => {
    const key = scopeKey(scope);
    // one run at a time: two write-capable agents can clobber each other
    if (Object.keys(get().runs).length > 0) {
      toast.info("Claude is still working. Stop that first.");
      return false;
    }
    const threadId = get().activeThread[key] ?? (await get().newThread(scope));
    if (!threadId) return false;

    const optimistic: ChatMessageDto = {
      id: -Date.now(),
      threadId,
      runId: "",
      seq: 0,
      role: "user",
      kind: "text",
      body: text,
      toolName: null,
      toolUseId: null,
      isError: false,
      truncated: false,
      inputTokens: 0,
      outputTokens: 0,
      costMicroUsd: 0,
      createdAt: Date.now(),
    };
    set((s) => ({ messages: { ...s.messages, [threadId]: [...(s.messages[threadId] ?? []), optimistic] } }));

    let runId: string;
    try {
      ({ runId } = await api.startRun(threadId, text, get().models?.current));
    } catch (err) {
      // the optimistic bubble must go too, or a rejected message sits in the
      // transcript looking sent until the next refetch
      set((s) => ({
        messages: {
          ...s.messages,
          [threadId]: (s.messages[threadId] ?? []).filter((m) => m.id !== optimistic.id),
        },
      }));
      toast.error(err instanceof Error ? err.message : "could not reach Claude");
      return false;
    }

    set((s) => ({
      runs: {
        ...s.runs,
        [threadId]: {
          runId,
          threadId,
          scopeKey: key,
          status: "starting",
          text: "",
          tools: [],
          notice: null,
          error: null,
          startedAt: Date.now(),
        },
      },
    }));

    const patch = (fn: (r: RunState) => RunState) =>
      set((s) => (s.runs[threadId] ? { runs: { ...s.runs, [threadId]: fn(s.runs[threadId]) } } : s));

    const stop = api.streamRun(
      runId,
      (e: AgentEvent) => {
        switch (e.t) {
          case "text.delta":
            patch((r) => ({ ...r, status: "streaming", text: r.text + e.text }));
            break;
          case "tool.start":
            patch((r) => ({
              ...r,
              status: "streaming",
              tools: [...r.tools, { id: e.toolUseId, name: e.name, input: e.input, status: "running" }],
            }));
            break;
          case "tool.end":
            patch((r) => ({
              ...r,
              tools: r.tools.map((t) =>
                t.id === e.toolUseId
                  ? { ...t, status: e.ok ? "ok" : "error", result: e.summary, truncated: e.truncated, durationMs: e.durationMs }
                  : t,
              ),
            }));
            break;
          case "notice":
            patch((r) => ({ ...r, notice: e.message }));
            break;
          case "error":
            patch((r) => ({ ...r, status: "error", error: e.message }));
            break;
          default:
            break;
        }
      },
      () => void finishRun(threadId, key, set, get),
    );
    stoppers.set(threadId, stop);
    return true;
  },

  stop: (threadId) => {
    const run = get().runs[threadId];
    if (!run) return;
    // the client closing its reader does not stop a server-side agent that is
    // mid edit, so tell the server too
    void api.cancelRun(run.runId);
    stoppers.get(threadId)?.();
    stoppers.delete(threadId);
  },
}));

/** Fold the finished run into the transcript and drop it from state. */
async function finishRun(
  threadId: string,
  key: string,
  set: (fn: (s: ChatState) => Partial<ChatState>) => void,
  get: () => ChatState,
): Promise<void> {
  stoppers.delete(threadId);
  set((s) => {
    const rest = { ...s.runs };
    delete rest[threadId];
    return { runs: rest };
  });
  // the server has the authoritative transcript, including tool rows
  try {
    const { messages } = await api.listMessages(threadId);
    set((s) => ({ messages: { ...s.messages, [threadId]: messages } }));
  } catch {
    // keep whatever is on screen
  }
  const scope: ChatScope = key === "workspace" ? { kind: "workspace", noteId: null } : { kind: "note", noteId: key.slice(5) };
  void get().loadThreads(scope);
}

/** True when any run is in flight, for the rail indicator. */
export const useAnyRunning = (): boolean => useChat((c) => Object.keys(c.runs).length > 0);
